/**
 * Where the user's plans live on the device, and the keys the move to an
 * account keeps for itself. CONTRACT §6 is the source of truth for the eight.
 */

/** The eight keys that move into the account and are then cleared (D5). */
export const DEVICE_KEYS = {
  schedule: 'mcw.schedule',
  saved: 'mcw.saved',
  hidden: 'mcw.hidden',
  personalEvents: 'mcw.personalEvents',
  attendance: 'mcw.attendance',
  travelLegs: 'mcw.travelLegs',
  home: 'mcw.home',
  eventEdits: 'mcw.eventEdits',
} as const;

export type DeviceField = keyof typeof DEVICE_KEYS;
export type DeviceKey = (typeof DEVICE_KEYS)[DeviceField];

export const DEVICE_FIELDS = Object.keys(DEVICE_KEYS) as DeviceField[];

/** The three lists, which become one intent per event in the account. */
export type ListField = 'schedule' | 'saved' | 'hidden';
export const LIST_FIELDS: ListField[] = ['schedule', 'saved', 'hidden'];

/** Everything else is a document, stored whole (`PUT /me/documents/:key`). */
export type DocKey = 'personalEvents' | 'attendance' | 'travelLegs' | 'home' | 'eventEdits';
export const DOC_KEYS: DocKey[] = ['personalEvents', 'attendance', 'travelLegs', 'home', 'eventEdits'];

export const isListField = (field: DeviceField): field is ListField =>
  field === 'schedule' || field === 'saved' || field === 'hidden';

const FIELD_OF_KEY = new Map<string, DeviceField>(
  DEVICE_FIELDS.map((field) => [DEVICE_KEYS[field], field]),
);

export const fieldOfKey = (key: string): DeviceField | undefined => FIELD_OF_KEY.get(key);

/** Old ids with no UUID yet. Never cleared; its value rides along in the import. */
export const LEGACY_UNMAPPED_KEY = 'mcw.legacyUnmapped';

/** The pending import: `{ workosUserId, importId, snapshot, createdAt }` (§6 step 2). */
export const IMPORT_RECORD_KEY = 'mcw.accountImport';

/** Changes made in account mode that the server hasn't confirmed yet. */
export const outboxKey = (workosUserId: string): string => `mcw.acct.${workosUserId}.outbox`;

/**
 * The WorkOS user whose account this browser last showed. While AuthKit is
 * still restoring the session, it tells the app to wait for the account rather
 * than draw the device's empty lists. Holds an opaque id, nothing else.
 */
export const ACCOUNT_HINT_KEY = 'mcw.acct.user';
